import { useEffect } from 'react';
import { useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, Mail, BookOpen, Hash } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { getLpConfig } from '@/pages/lp/lpConfig';

/**
 * LandingPageThankYou — shared thank-you page rendered after a successful LP submission.
 * The URL `/lp/<slug>/thank-you` is the Google Ads conversion goal — marketers configure
 * a separate conversion tag in GTM that fires on this page-view for each LP.
 *
 * Fires:
 *   dataLayer.push({event: 'lp_thank_you_view', lp_slug: '<slug>'})
 * on mount, so Google Ads / GA4 can count it as the conversion.
 */
const LandingPageThankYou = () => {
  const { slug } = useParams();
  const config = getLpConfig(slug);
  const [searchParams] = useSearchParams();
  const rawRef = searchParams.get("ref") || "";
  // KGOB-LP-XXXXXX — first 6 hex chars of the UUID, uppercase. Hides full UUID, easy to read aloud.
  const refNumber = rawRef && rawRef !== "honeypot"
    ? `KGOB-LP-${rawRef.replace(/-/g, "").slice(0, 6).toUpperCase()}`
    : null;

  useEffect(() => {
    if (typeof window === "undefined" || !config) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "lp_thank_you_view",
      lp_slug: config.slug,
    });

    // Defense-in-depth: ensure robots noindex is present (Helmet meta override is unreliable site-wide).
    let robots = document.querySelector('meta[name="robots"]');
    const prev = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex,follow');
    return () => {
      if (prev) robots.setAttribute('content', prev);
      else robots.remove();
    };
  }, [config]);

  if (!config) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Thank you — KGOB</title>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={`https://kgob.com/lp/${config.slug}/thank-you`} />
      </Helmet>

      <Header />

      <main className="py-16 lg:py-24" data-testid={`lp-thank-you-${config.slug}`}>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle2 className="w-9 h-9 text-green-600" strokeWidth={2} />
            </div>

            <h1
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold text-[#0B2240] mb-4"
              data-testid="lp-thank-you-headline"
            >
              {config.thank_you_headline}
            </h1>

            <p className="text-base sm:text-lg text-[#4C5A70] max-w-2xl mx-auto mb-6">
              {config.thank_you_subhead}
            </p>

            {refNumber && (
              <div
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E8E5DC] px-4 py-2 mb-10 shadow-sm"
                data-testid="lp-thank-you-ref"
              >
                <Hash className="h-3.5 w-3.5 text-[#C9A961]" />
                <span className="text-xs uppercase tracking-wider text-[#4C5A70]">Reference</span>
                <span className="font-mono text-sm font-semibold text-[#0B2240]">{refNumber}</span>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="rounded-2xl bg-white border border-[#E8E5DC] shadow-lg p-6 sm:p-8 mb-8"
          >
            <h2 className="font-['Playfair_Display'] text-xl sm:text-2xl font-semibold text-[#0B2240] mb-4">
              Need to reach us sooner?
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <a
                href={`tel:${config.phone_dial}`}
                className="inline-flex items-center gap-3 rounded-xl bg-[#F7F6F2] border border-[#E8E5DC] px-4 py-3 hover:border-[#C9A961] transition"
                data-testid="lp-thank-you-phone"
              >
                <Phone className="h-5 w-5 text-[#C9A961]" />
                <div className="text-left">
                  <div className="text-xs uppercase tracking-wider text-[#4C5A70]">Call us</div>
                  <div className="text-sm font-semibold text-[#0B2240]">{config.phone_display}</div>
                </div>
              </a>
              <a
                href={`mailto:${config.email}`}
                className="inline-flex items-center gap-3 rounded-xl bg-[#F7F6F2] border border-[#E8E5DC] px-4 py-3 hover:border-[#C9A961] transition"
                data-testid="lp-thank-you-email"
              >
                <Mail className="h-5 w-5 text-[#C9A961]" />
                <div className="text-left">
                  <div className="text-xs uppercase tracking-wider text-[#4C5A70]">Email us</div>
                  <div className="text-sm font-semibold text-[#0B2240]">{config.email}</div>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center"
          >
            <p className="text-sm text-[#4C5A70] mb-3">While you wait, browse our CPA answer library:</p>
            <Link
              to="/get-your-answer/library"
              className="inline-flex items-center gap-2 rounded-full bg-[#0B2240] hover:bg-[#0d2a55] text-white text-sm font-medium px-5 py-2.5 transition"
              data-testid="lp-thank-you-library-cta"
            >
              <BookOpen className="h-4 w-4" />
              Explore the KGOB Library
            </Link>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPageThankYou;
